import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AIReport from './AIReport';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart3, Users, Calendar, DollarSign, Download, TrendingUp,
  FileText, Building2, GraduationCap, Clock, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const CHART_COLORS = ['#DC2626', '#2563EB', '#16A34A', '#CA8A04', '#7C3AED', '#EC4899', '#06B6D4', '#84CC16'];

export default function Reports() {
  const { selectedEntity, entities } = useOutletContext() || {};
  const [stats, setStats] = useState(null);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('overview');

  useEffect(() => {
    fetchReportData();
  }, [selectedEntity]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const entityQuery = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      
      const [statsRes, trendRes, complianceRes, empRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/stats${entityQuery}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/attendance-trend?days=30${selectedEntity ? `&entity_id=${selectedEntity.entity_id}` : ''}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/compliance-status${entityQuery}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/employees${entityQuery}`, { headers, credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (trendRes.ok) setAttendanceTrend(await trendRes.json());
      if (complianceRes.ok) setCompliance(await complianceRes.json());
      if (empRes.ok) setEmployees(await empRes.json());
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const getQualificationData = () => {
    const qualifications = {};
    employees.forEach(emp => {
      const qual = emp.highest_qualification || 'Not Specified';
      qualifications[qual] = (qualifications[qual] || 0) + 1;
    });
    return Object.entries(qualifications).map(([name, value]) => ({ name, value }));
  };

  const getAppointmentTypeData = () => {
    const types = {};
    employees.forEach(emp => {
      const type = emp.appointment_type || 'Not Specified';
      types[type] = (types[type] || 0) + 1;
    });
    return Object.entries(types).map(([name, value]) => ({ name, value }));
  };

  const getExperienceDistribution = () => {
    const ranges = {
      '0-5 yrs': 0,
      '5-10 yrs': 0,
      '10-15 yrs': 0,
      '15-20 yrs': 0,
      '20+ yrs': 0
    };
    employees.forEach(emp => {
      const exp = emp.experience_years || 0;
      if (exp <= 5) ranges['0-5 yrs']++;
      else if (exp <= 10) ranges['5-10 yrs']++;
      else if (exp <= 15) ranges['10-15 yrs']++;
      else if (exp <= 20) ranges['15-20 yrs']++;
      else ranges['20+ yrs']++;
    });
    return Object.entries(ranges).map(([range, count]) => ({ range, count }));
  };

  const [customMonth, setCustomMonth] = useState(new Date().toISOString().slice(0, 7));
  const [customDept, setCustomDept] = useState('');

  const exportReport = () => {
    toast.success('Report export started');
  };

  const downloadCustomReport = async (reportType) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const params = new URLSearchParams({ report_type: reportType });
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (customDept) params.append('department', customDept);
      if (customMonth) params.append('month', customMonth);
      const res = await fetch(`${API_URL}/api/reports/custom/download?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${reportType}_report.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else toast.error('Failed to download report');
    } catch { toast.error('Download failed'); }
  };

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-sm" />
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Reports & Analytics
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Comprehensive HR insights and compliance reports
          </p>
        </div>
        <Button className="btn-primary" onClick={exportReport} data-testid="export-report-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Report Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="workforce" data-testid="tab-workforce">Workforce</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom">Custom Reports</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <span className="inline-flex items-center gap-1">AI Report <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0">AI</Badge></span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Staff</p>
                    <p className="text-2xl font-bold">{stats?.total_employees || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Attendance Rate</p>
                    <p className="text-2xl font-bold text-green-600">{stats?.attendance_rate || 0}%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending Leaves</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats?.pending_leaves || 0}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Entities</p>
                    <p className="text-2xl font-bold">{entities?.length || 0}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#DC2626]" />
                  Staff by Department
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.department_stats || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="department" type="category" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#DC2626" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#DC2626]" />
                  Staff by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats?.category_stats || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="category"
                        label={({ category, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {(stats?.category_stats || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Workforce Tab */}
        <TabsContent value="workforce" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Qualification Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#DC2626]" />
                  Qualification Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getQualificationData()}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label
                      >
                        {getQualificationData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Appointment Type */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#DC2626]" />
                  Appointment Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getAppointmentTypeData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Experience Distribution */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#DC2626]" />
                  Experience Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={getExperienceDistribution()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="count" stroke="#DC2626" fill="#DC2626" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#DC2626]" />
                30-Day Attendance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={attendanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="present" stroke="#16A34A" strokeWidth={2} name="Present" />
                    <Line type="monotone" dataKey="absent" stroke="#DC2626" strokeWidth={2} name="Absent" />
                    <Line type="monotone" dataKey="on_leave" stroke="#CA8A04" strokeWidth={2} name="On Leave" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          {compliance && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">UGC Compliance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#DC2626]">{compliance.ugc_compliance_rate}%</p>
                    <p className="text-sm text-slate-500">Approved Staff</p>
                  </div>
                  <Progress value={compliance.ugc_compliance_rate} className="h-3" />
                  <div className="flex justify-between text-sm">
                    <span>{compliance.ugc_approved} approved</span>
                    <span>{compliance.total_staff} total</span>
                  </div>
                  <Badge className={compliance.ugc_compliance_rate >= 80 ? 'badge-success' : 'badge-warning'}>
                    {compliance.ugc_compliance_rate >= 80 ? 'Compliant' : 'Needs Improvement'}
                  </Badge>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">AICTE Compliance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-blue-600">{compliance.aicte_compliance_rate}%</p>
                    <p className="text-sm text-slate-500">Approved Staff</p>
                  </div>
                  <Progress value={compliance.aicte_compliance_rate} className="h-3" />
                  <div className="flex justify-between text-sm">
                    <span>{compliance.aicte_approved} approved</span>
                    <span>{compliance.total_staff} total</span>
                  </div>
                  <Badge className={compliance.aicte_compliance_rate >= 80 ? 'badge-success' : 'badge-warning'}>
                    {compliance.aicte_compliance_rate >= 80 ? 'Compliant' : 'Needs Improvement'}
                  </Badge>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Qualification Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-green-600">{compliance.qualification_rate}%</p>
                    <p className="text-sm text-slate-500">Qualified Staff</p>
                  </div>
                  <Progress value={compliance.qualification_rate} className="h-3" />
                  <div className="flex justify-between text-sm">
                    <span>{compliance.qualified_staff} qualified</span>
                    <span>{compliance.total_staff} total</span>
                  </div>
                  <Badge className={compliance.qualification_rate >= 80 ? 'badge-success' : 'badge-warning'}>
                    {compliance.qualification_rate >= 80 ? 'Excellent' : 'Needs Improvement'}
                  </Badge>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Compliance Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {compliance && compliance.ugc_compliance_rate < 100 && (
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="font-medium text-yellow-800 dark:text-yellow-400">UGC Approval Pending</p>
                      <p className="text-sm text-yellow-600">{compliance.total_staff - compliance.ugc_approved} staff members need UGC approval</p>
                    </div>
                  </div>
                )}
                {compliance && compliance.aicte_compliance_rate < 100 && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <AlertTriangle className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-400">AICTE Approval Pending</p>
                      <p className="text-sm text-blue-600">{compliance.total_staff - compliance.aicte_approved} staff members need AICTE approval</p>
                    </div>
                  </div>
                )}
                {compliance && compliance.qualification_rate < 80 && (
                  <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-400">Qualification Gap</p>
                      <p className="text-sm text-red-600">Consider hiring more PhD/M.Tech qualified faculty</p>
                    </div>
                  </div>
                )}
                {compliance && compliance.ugc_compliance_rate >= 100 && compliance.aicte_compliance_rate >= 100 && compliance.qualification_rate >= 80 && (
                  <div className="text-center py-8 text-green-600">
                    <p className="font-medium">All compliance metrics are within acceptable limits</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Reports Tab */}
        <TabsContent value="custom" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-[#DC2626]" />
                Download Custom Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Department</label>
                  <Select value={customDept} onValueChange={setCustomDept}>
                    <SelectTrigger className="w-[180px]" data-testid="custom-dept-filter"><SelectValue placeholder="All Departments" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=" ">All Departments</SelectItem>
                      {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Month (Salary)</label>
                  <input type="month" className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={customMonth} onChange={e => setCustomMonth(e.target.value)} data-testid="custom-month-filter" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-2 border-dashed hover:border-[#DC2626] transition-colors cursor-pointer group" onClick={() => downloadCustomReport('employee_details')}>
                  <CardContent className="p-6 text-center" data-testid="report-employee-details">
                    <Users className="w-10 h-10 mx-auto text-slate-400 group-hover:text-[#DC2626] transition-colors mb-3" />
                    <h3 className="font-semibold">Employee Details</h3>
                    <p className="text-xs text-slate-500 mt-1">Name, email, phone, department, designation, DOJ, qualification</p>
                    <Button variant="outline" size="sm" className="mt-3"><Download className="w-3.5 h-3.5 mr-1" />Download CSV</Button>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed hover:border-[#DC2626] transition-colors cursor-pointer group" onClick={() => downloadCustomReport('bank_details')}>
                  <CardContent className="p-6 text-center" data-testid="report-bank-details">
                    <Building2 className="w-10 h-10 mx-auto text-slate-400 group-hover:text-[#DC2626] transition-colors mb-3" />
                    <h3 className="font-semibold">Bank Details</h3>
                    <p className="text-xs text-slate-500 mt-1">Name, bank name, account number, IFSC, PAN, basic salary</p>
                    <Button variant="outline" size="sm" className="mt-3"><Download className="w-3.5 h-3.5 mr-1" />Download CSV</Button>
                  </CardContent>
                </Card>
                <Card className="border-2 border-dashed hover:border-[#DC2626] transition-colors cursor-pointer group" onClick={() => downloadCustomReport('monthly_salary')}>
                  <CardContent className="p-6 text-center" data-testid="report-monthly-salary">
                    <DollarSign className="w-10 h-10 mx-auto text-slate-400 group-hover:text-[#DC2626] transition-colors mb-3" />
                    <h3 className="font-semibold">Monthly Salary Report</h3>
                    <p className="text-xs text-slate-500 mt-1">Basic, gross, PF, TDS, deductions, net salary, status</p>
                    <Button variant="outline" size="sm" className="mt-3"><Download className="w-3.5 h-3.5 mr-1" />Download CSV</Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Report Tab (merged from /ai-report) */}
        <TabsContent value="ai" className="space-y-6">
          <AIReport embedded />
        </TabsContent>

      </Tabs>
    </div>
  );
}
